import { RefObject, useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useFocusTrap(ref: RefObject<HTMLElement | null>, isOpen: boolean): void {
    useEffect(() => {
        if (!isOpen) return;
        const node = ref.current;
        if (!node) return;

        const getFocusable = (): HTMLElement[] =>
            Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
                (el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null,
            );

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;
            const focusable = getFocusable();
            if (focusable.length === 0) {
                event.preventDefault();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement as HTMLElement | null;
            const insideTrap = active !== null && node.contains(active);
            if (event.shiftKey) {
                if (!insideTrap || active === first) {
                    event.preventDefault();
                    last.focus();
                }
            } else {
                if (!insideTrap || active === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [ref, isOpen]);
}
